import { SkeletonSpinnerCentered } from '@civilgrid/ui/SkeletonLoader';
import { useParams } from '@tanstack/react-router';
import type { Point } from 'geojson';
import { useEffect, useMemo, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { useIsMounted } from '~/hooks/useIsMounted';
import rootStore from '~/store';
import layerStore from '~/store2/layers';
import { setTabSyncedLayerVisible, useRestoreLayerOnUnmount } from '~/store2/tabLayerLocks';
import { query } from './common';

interface Props {
  title: string;
  layerId: string;
  fields: string[];
  description: string | JSX.Element;
  setFeatureSize?: (layerId: string, size: number) => void;
}

export default function LayerSection({ title, layerId, fields, description, setFeatureSize }: Props) {
  const { projectDetailsMap } = useMap();
  const { projectId } = useParams({ strict: false }) as { projectId?: string };
  const [isLoading, setLoading] = useState(true);
  const [queryResponse, setQueryResponse] = useState<GeoJSON.Feature[]>([]);
  const { tilesConfig, visibleLayerIds } = useSnapshot(layerStore) as typeof layerStore;

  const isMounted = useIsMounted();

  async function runQuery() {
    if (projectDetailsMap) {
      setLoading(true);
      const bounds = projectDetailsMap.getBounds()?.toArray();
      //@ts-expect-error
      const data = await query(layerId, bounds, projectDetailsMap.getZoom());
      if (isMounted()) {
        setFeatureSize?.(layerId, data.length);
        setQueryResponse(data);
        setLoading(false);
      }
    }
  }

  // On unmount, return the layer to picker-controlled visibility (the in-tab toggle below
  // must not clobber a layer the picker had on before entering the tab).
  useRestoreLayerOnUnmount(layerId);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    runQuery();
    // uncomment below to re-run query when map moves
    // const delayedRunQuery = debounce(runQuery, 1_000);
    // projectDetailsMap?.on('moveend', delayedRunQuery);
    // return () => {
    //   projectDetailsMap?.off('moveend', delayedRunQuery);
    // };
  }, [projectDetailsMap?.loaded]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const content = useMemo(() => {
    if (queryResponse.length > 0) {
      if (queryResponse.length > 100) {
        return <div>Too many results to display. Zoom in or move map.</div>;
      }

      const layerConfig = tilesConfig?.[layerId];
      const tableHeadings = fields.map((f) => (
        <th className="border p-1" key={f}>
          {layerConfig?.fields[f].alias}
        </th>
      ));
      const tableBody = queryResponse.map((r) => (
        <tr className="group/tr" key={r.id}>
          {fields.map((f, i) => (
            <td key={f} className={`relative max-w-[100px] overflow-hidden text-ellipsis border p-1 `}>
              {r.properties![f]}
              {i === 0 && r.geometry.type === 'Point' && (
                <div
                  className="absolute left-0 top-0 my-2 hidden cursor-pointer rounded bg-accent-400 p-2 group-hover/tr:block"
                  onClick={() =>
                    projectDetailsMap?.flyTo({
                      center: (r.geometry as Point).coordinates as [number, number],
                      zoom: 22,
                    })
                  }
                >
                  View in map
                </div>
              )}
            </td>
          ))}
        </tr>
      ));
      return (
        <table className="w-full table-auto border-collapse border text-xs">
          <thead>
            <tr className="border text-left text-xs">{tableHeadings}</tr>
          </thead>
          <tbody>{tableBody}</tbody>
        </table>
      );
    } else {
      return <div className="text-sm">No data in current map view - zoom out for more data.</div>;
    }
  }, [queryResponse, tilesConfig]);

  return (
    <div className="group w-full p-4">
      <div className=" flex flex-row items-center space-x-2">
        <div className="w-4/5 py-2 text-base font-semibold">{title}</div>
        {queryResponse.length > 0 && (
          <div
            data-testid={`environmental-layer-toggle-${layerId}`}
            className="cursor-pointer text-xs text-blue-50 underline hover:text-blue-60"
            onClick={() => {
              const willBeVisible = !visibleLayerIds.has(layerId);
              setTabSyncedLayerVisible(layerId, willBeVisible);
              if (projectId) {
                captureTypedPosthogEvent(POSTHOG_EVENTS.projectEnvironmentalLayerToggled, {
                  project_id: projectId,
                  organization_id: rootStore.currentOrganizationId || undefined,
                  layer_id: layerId,
                  layer_name: title,
                  enabled: willBeVisible,
                  source: 'env_tab',
                });
              }
            }}
          >
            {visibleLayerIds.has(layerId) ? 'Hide on Map' : 'Show on Map'}
          </div>
        )}
      </div>
      {isLoading ? (
        <SkeletonSpinnerCentered />
      ) : (
        <div className="flex flex-col space-y-4">
          <div className="">{content}</div>
          {queryResponse.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="text-sm font-bold">Analysis</div>
              <div className="text-sm">{description}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
